/** Theme type — the two supported themes. */
export type Theme = "light" | "dark";

const STORAGE_KEY = "timlul-theme";
const VALID: Set<string> = new Set<Theme>(["light", "dark"]);

/**
 * localStorage helpers that swallow QuotaExceededError and
 * SecurityError from private-browsing / storage-blocked contexts.
 * Use these everywhere instead of bare localStorage.setItem/getItem.
 */
export function safeStorageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private-browsing quota or blocked — silently ignore.
  }
}

export function safeStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Decide the initial theme from two inputs.
 *
 * Priority: stored value (if valid) > system prefersDark.
 *
 * @param stored  - value read from localStorage (or null)
 * @param prefersDark - result of matchMedia("(prefers-color-scheme: dark)").matches
 */
export function resolveInitialTheme(
  stored: string | null,
  prefersDark: boolean
): Theme {
  if (stored !== null && VALID.has(stored)) {
    return stored as Theme;
  }
  return prefersDark ? "dark" : "light";
}

/**
 * Apply a theme to the document and persist it.
 * Sets data-theme on <html> and writes to localStorage (safe: no throw in
 * private-browsing / quota-exceeded contexts).
 */
export function applyTheme(t: Theme): void {
  document.documentElement.dataset.theme = t;
  safeStorageSet(STORAGE_KEY, t);
}

/**
 * Read the environment, resolve the theme, apply it, and return the result.
 * Call once from main.tsx before rendering.
 */
export function initTheme(): Theme {
  const stored = safeStorageGet(STORAGE_KEY);
  const prefersDark =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
  const theme = resolveInitialTheme(stored, prefersDark);
  applyTheme(theme);
  return theme;
}
